import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useExam } from "@/context/exam-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  BookOpen,
  CheckCircle,
  Award,
  Clock,
  Users,
  BarChart3,
  Calendar,
  Star,
  ArrowRight,
  ChevronRight,
  Search,
  Sparkles,
  Brain,
  GraduationCap,
  Rocket,
  LucideIcon,
} from "lucide-react";

// Import missing components
import StatsSection from "@/components/StatsSection";
import FeaturesSection from "@/components/FeaturesSection";
import CoursesSection from "@/components/CoursesSection";
import HowItWorksSection from "@/components/HowItWorksSection";
import TestimonialsSection from "@/components/TestimonialsSection";
import UpcomingExamsSection from "@/components/UpcomingExamsSection";
import FAQSection from "@/components/FAQSection";
import CTASection from "@/components/CTASection";
import Footer from "@/components/Footer";

export default function HomePage() {
  const { exams, user } = useExam();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  // Filter exams based on status
  const upcomingExams = exams
    .filter(exam => {
      const now = new Date();
      return exam.isActive && exam.startDate && exam.startDate > now;
    })
    .sort((a, b) => {
      const dateA = a.startDate || new Date();
      const dateB = b.startDate || new Date();
      return dateA.getTime() - dateB.getTime();
    })
    .slice(0, 6);

  // Categories for courses/exams
  const categories = [
    { id: "all", name: "All Categories" },
    { id: "math", name: "Mathematics" },
    { id: "science", name: "Science" },
    { id: "language", name: "Language Arts" },
    { id: "technology", name: "Technology" }
  ];
  
  // Sample courses data
  const courses = [
    {
      id: 1,
      title: "Mathematics Mastery",
      category: "math",
      image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb",
      description: "Master key mathematical concepts and problem-solving techniques",
      rating: 4.8,
      students: 1245,
      instructor: "Dr. Sarah Johnson",
      level: "Intermediate"
    },
    {
      id: 2,
      title: "Physics Fundamentals",
      category: "science",
      image: "https://images.unsplash.com/photo-1636466497217-26a8cbeaf0aa",
      description: "Learn essential physics principles with practical applications",
      rating: 4.7,
      students: 825,
      instructor: "Prof. Michael Chen",
      level: "Advanced"
    },
    {
      id: 3,
      title: "Web Development Bootcamp",
      category: "technology",
      image: "https://images.unsplash.com/photo-1627398242454-45a1465c2479",
      description: "Comprehensive course covering front-end and back-end development",
      rating: 4.9,
      students: 2340,
      instructor: "Alex Rodriguez",
      level: "Beginner"
    },
    {
      id: 4,
      title: "Advanced Chemistry",
      category: "science",
      image: "https://images.unsplash.com/photo-1603126857599-f6e157fa2fe6",
      description: "Deep dive into chemical principles and laboratory techniques",
      rating: 4.6,
      students: 915,
      instructor: "Dr. Emily Zhang",
      level: "Advanced"
    },
    {
      id: 5,
      title: "Creative Writing Workshop",
      category: "language",
      image: "https://images.unsplash.com/photo-1455390582262-044cdead277a",
      description: "Develop your creative writing skills and storytelling techniques",
      rating: 4.8,
      students: 1120,
      instructor: "James Wilson",
      level: "All Levels"
    },
    {
      id: 6,
      title: "Data Science Fundamentals",
      category: "technology",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
      description: "Learn data analysis, visualization, and machine learning basics",
      rating: 4.9,
      students: 1850,
      instructor: "Dr. Lisa Kumar",
      level: "Intermediate"
    }
  ];

  // Features list
  const features = [
    {
      title: "Personalized Learning",
      description: "Adaptive paths tailored to your specific needs",
      icon: Brain
    },
    {
      title: "Expert Instructors",
      description: "Learn from industry professionals",
      icon: GraduationCap
    },
    {
      title: "Real-time Progress Tracking",
      description: "Monitor your performance with detailed analytics",
      icon: BarChart3
    },
    {
      title: "Comprehensive Exams",
      description: "Test your knowledge with realistic simulations",
      icon: CheckCircle
    }
  ];

  // Stats
  const stats = [
    { label: "Active Users", value: "10,000+", icon: Users },
    { label: "Success Rate", value: "94%", icon: Award },
    { label: "Total Exams", value: exams.length.toString(), icon: BookOpen },
    { label: "Hours Saved", value: "120,000+", icon: Clock }
  ];

  // Filter courses based on active tab
  const filteredCourses = activeTab === "all" 
    ? courses 
    : courses.filter(course => course.category === activeTab);

  return (
    <div className="min-h-screen">
      {/* Hero section */}
      <section className="relative pt-20 pb-32 overflow-hidden bg-gradient-to-br from-primary/5 to-primary/10">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-white/80 dark:bg-black/80" />
        </div>

        <div className="container relative z-10 mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12 items-center justify-between">
            <div className="max-w-2xl">
              <Badge className="mb-6 px-3 py-1" variant="outline">
                <Sparkles className="h-3.5 w-3.5 mr-1" />
                <span className="text-sm">Your Path to Success</span>
              </Badge>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Master Your Exams with <span className="text-primary">Confidence</span>
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground mb-8">
                Comprehensive exam preparation platform with personalized learning, 
                real-time progress tracking, and expert guidance.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button size="lg" asChild>
                  <Link to={user ? "/dashboard" : "/register"}>
                    Get Started
                  </Link>
              </Button>
                <Button size="lg" variant="outline" asChild>
                <Link to="/exams" className="flex items-center">
                    Browse Exams
                    <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              </div>
            </div>
            
            <div className="relative w-full max-w-md">
              <div className="relative z-10 bg-background rounded-xl shadow-xl overflow-hidden">
                <div className="p-1 bg-gradient-to-r from-primary to-primary/50 rounded-t-xl">
                  <div className="flex justify-between items-center px-4 py-2 rounded-t-lg bg-primary/10">
                    <div className="flex space-x-1">
                      <div className="h-2 w-2 rounded-full bg-red-500" />
                      <div className="h-2 w-2 rounded-full bg-yellow-500" />
                      <div className="h-2 w-2 rounded-full bg-green-500" />
                    </div>
                    <div className="text-xs font-medium text-primary-foreground/80">Exam Dashboard</div>
                    <div></div>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="mb-6">
                    <h3 className="font-semibold mb-2">Upcoming Exams</h3>
                    <div className="space-y-3">
                      {upcomingExams.slice(0, 3).map((exam, i) => (
                        <div 
                          key={exam.id} 
                          className="flex items-center justify-between p-2 rounded-lg bg-muted/50 border border-border/50"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                              {i + 1}
                            </div>
                            <div className="truncate">
                              <p className="font-medium truncate">{exam.title}</p>
                              <p className="text-xs text-muted-foreground">
                                {exam.startDate?.toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <Badge>{exam.duration} min</Badge>
                        </div>
                      ))}
                      
                      {upcomingExams.length === 0 && (
                        <div className="text-center py-2 text-muted-foreground text-sm">
                          No upcoming exams
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <h3 className="font-semibold">Progress</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Completion</span>
                        <span className="font-medium">68%</span>
                      </div>
                      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                        <div className="bg-primary h-full rounded-full" style={{ width: '68%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="absolute -right-4 -bottom-4 w-40 h-40 bg-primary/10 rounded-full blur-3xl" />
              <div className="absolute -left-8 -top-8 w-60 h-60 bg-primary/5 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats section */}
      <section className="py-12 bg-background border-y">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <stat.icon className="h-6 w-6 text-primary" />
                </div>
                <p className="text-3xl font-bold">{stat.value}</p>
                <p className="text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="mb-4" variant="outline">Features</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Everything You Need to Succeed</h2>
            <p className="text-lg text-muted-foreground">
              Our platform provides comprehensive tools and resources to help you excel in your exams.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="bg-background/50 border border-border/50 transition-all hover:shadow-md">
                <CardHeader>
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Exams section */}
      <section className="py-20 bg-gradient-to-b from-background to-background/50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
            <div>
              <Badge className="mb-2" variant="outline">Exams</Badge>
              <h2 className="text-3xl font-bold">Upcoming Exams</h2>
              <p className="text-muted-foreground">Prepare for these upcoming exams to test your knowledge.</p>
        </div>

            <Button variant="outline" asChild>
              <Link to="/exams">View All Exams</Link>
            </Button>
          </div>
          
          <div className="relative">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {upcomingExams.length > 0 ? (
                upcomingExams.map((exam) => (
                  <Card key={exam.id} className="h-full hover:shadow-md transition-shadow overflow-hidden">
                    {exam.thumbnailUrl && (
                      <div className="h-40 w-full overflow-hidden">
                        <img 
                          src={exam.thumbnailUrl} 
                          alt={exam.title}
                          className="w-full h-full object-cover transition-transform hover:scale-105"
                        />
                      </div>
                    )}
                    <CardHeader>
                      <div className="flex justify-between items-start gap-2">
                        <CardTitle className="text-xl">{exam.title}</CardTitle>
                        <Badge>{exam.duration} min</Badge>
                      </div>
                      <CardDescription className="line-clamp-2">{exam.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center text-sm">
                        <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span>{exam.startDate?.toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <BookOpen className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span>{exam.totalQuestions || 0} Questions</span>
                      </div>
                    </CardContent>
                    <CardFooter>
                      <Button asChild className="w-full">
                        <Link to={`/exams/${exam.id}`}>View Details</Link>
                      </Button>
                    </CardFooter>
                  </Card>
                ))
              ) : (
                <div className="col-span-full text-center py-12 text-muted-foreground">
                  <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>No upcoming exams available at the moment.</p>
                  <p className="text-sm">Check back later or contact administration for more information.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Courses section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col space-y-6 mb-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <Badge className="mb-2" variant="outline">Courses</Badge>
                <h2 className="text-3xl font-bold">Popular Courses</h2>
                <p className="text-muted-foreground">Enhance your knowledge with our expert-led courses.</p>
              </div>
              
              <div className="relative w-full md:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search courses..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-8">
                {categories.map(category => (
                  <TabsTrigger key={category.id} value={category.id}>
                    {category.name}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              <TabsContent value={activeTab} className="mt-0">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredCourses.map((course) => (
                    <Card key={course.id} className="h-full hover:shadow-md transition-shadow overflow-hidden">
                      <div className="h-40 w-full overflow-hidden relative">
                        <img 
                          src={course.image} 
                          alt={course.title}
                          className="w-full h-full object-cover transition-transform hover:scale-105"
                        />
                        <div className="absolute top-2 right-2">
                          <Badge variant="secondary" className="bg-background/80 backdrop-blur-sm">
                            {course.level}
                          </Badge>
                        </div>
                      </div>
                      <CardHeader>
                        <div className="flex justify-between items-start gap-2">
                          <CardTitle className="text-xl">{course.title}</CardTitle>
                          <div className="flex items-center">
                            <Star className="h-4 w-4 text-amber-500 mr-1 fill-amber-500" />
                            <span className="text-sm font-medium">{course.rating.toFixed(1)}</span>
                          </div>
                        </div>
                        <CardDescription className="line-clamp-2">{course.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-2 mb-4">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback>{course.instructor[0]}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium">{course.instructor}</span>
                        </div>
                        
                        <div className="flex items-center text-sm text-muted-foreground">
                          <Users className="h-4 w-4 mr-1" />
                          <span>{course.students.toLocaleString()} students</span>
                        </div>
                      </CardContent>
                      <CardFooter>
                        <Button asChild className="w-full">
                          <Link to="/courses">Enroll Now</Link>
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
          
          <div className="text-center mt-8">
            <Button variant="outline" asChild>
              <Link to="/courses" className="flex items-center">
                Browse All Courses
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA section */}
      <section className="py-20 bg-primary/5 border-y">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-6" variant="outline">Get Started Today</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Excel in Your Exams?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join thousands of students who have improved their exam scores and achieved academic success.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" asChild>
                <Link to={user ? "/dashboard" : "/register"}>
                  {user ? "Go to Dashboard" : "Register Now"}
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer with simple links */}
      <footer className="py-12 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center text-xl font-bold">
              <Rocket className="h-6 w-6 mr-2 text-primary" />
              ExamPrep
            </div>
            
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-2">
              <Link to="/about" className="text-muted-foreground hover:text-foreground transition-colors">
                About
              </Link>
              <Link to="/courses" className="text-muted-foreground hover:text-foreground transition-colors">
                Courses
              </Link>
              <Link to="/exams" className="text-muted-foreground hover:text-foreground transition-colors">
                Exams
              </Link>
              <Link to="/blog" className="text-muted-foreground hover:text-foreground transition-colors">
                Blog
              </Link>
              <Link to="/contact" className="text-muted-foreground hover:text-foreground transition-colors">
                Contact
              </Link>
            </div>
            
            <div className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} ExamPrep. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}